import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from 'lucide-react'
import type { ReactNode } from 'react'
import { ToastContext } from '@/components/ui/toast-context'
import type { Toast, ToastOptions, ToastTone } from '@/components/ui/toast-context'
import { cn } from '@/lib/cn'

const DEFAULT_DURATION = 5000

const TONE_STYLES: Record<ToastTone, string> = {
  success: 'border-success-500/40 bg-success-50 text-success-700',
  error: 'border-danger-500/40 bg-danger-50 text-danger-700',
  warning: 'border-warning-500/40 bg-warning-50 text-warning-700',
  info: 'border-info-500/40 bg-info-50 text-info-700',
}

const TONE_ICONS: Record<ToastTone, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertTriangle,
  info: Info,
}

/**
 * Transient confirmations ("Product published"). Anything the user must act on
 * belongs in the page, not here — a toast is easy to miss and impossible to revisit.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id)
    if (timer) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
    setToasts((current) => current.filter((entry) => entry.id !== id))
  }, [])

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = crypto.randomUUID()
      setToasts((current) => [...current, { ...options, id }])

      const duration = options.duration ?? DEFAULT_DURATION
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), duration),
        )
      }
      return id
    },
    [dismiss],
  )

  // Timers outlive React state, so they have to be cleared explicitly on unmount.
  useEffect(() => {
    const pending = timers.current
    return () => {
      for (const timer of pending.values()) clearTimeout(timer)
      pending.clear()
    }
  }, [])

  const value = useMemo(() => ({ toast, dismiss }), [toast, dismiss])

  return (
    <ToastContext value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext>
  )
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: Toast[]
  onDismiss: (id: string) => void
}) {
  if (typeof document === 'undefined') return null

  return createPortal(
    <div
      // pointer-events-none on the stack, auto on each toast, so the region never
      // swallows clicks aimed at the page underneath it.
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-3 sm:inset-x-auto sm:right-0 sm:items-end"
    >
      {toasts.map((entry) => {
        const tone = entry.tone ?? 'info'
        const Icon = TONE_ICONS[tone]

        return (
          <div
            key={entry.id}
            // Errors interrupt; everything else waits its turn.
            role={tone === 'error' ? 'alert' : 'status'}
            aria-live={tone === 'error' ? 'assertive' : 'polite'}
            className={cn(
              'pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-lg border px-3 py-2.5 shadow-raised',
              TONE_STYLES[tone],
            )}
          >
            <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />

            <div className="min-w-0 flex-1">
              <p className="font-medium">{entry.title}</p>
              {entry.description ? (
                <p className="mt-0.5 text-ink-muted">{entry.description}</p>
              ) : null}
            </div>

            <button
              type="button"
              onClick={() => onDismiss(entry.id)}
              className="-mr-1 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100"
            >
              <X className="size-3.5" aria-hidden="true" />
              <span className="sr-only">Dismiss notification</span>
            </button>
          </div>
        )
      })}
    </div>,
    document.body,
  )
}
